<script lang="ts">
    import type Site from '@/schemas/public/Site.ts';
    import type Blocks from '@/schemas/public/Blocks.ts';
    import type {Field} from "@/types/types";
    import FormContainer from "@/components/Form/FormContainer.svelte";
    import Debug from "@/components/Debug.svelte";
    import {useForm} from '@inertiajs/svelte';
    import InlineEditable from "@/components/Inputs/InlineEditable.svelte";

    import FormBlock from "@/components/Form/FormBlock.svelte";
    import Popover from "@/components/Popover.svelte";
    import AppLayout from "@/layouts/AppLayout.svelte";
    import {Pencil, Plus, Trash} from "lucide-svelte";
    import EditSiteModal from "@/components/sites/EditSiteModal.svelte";
    import CreateRecordModal from "@/components/CreateRecordModal.svelte";
    import CreateSiteModal from "@/components/sites/CreateSiteModal.svelte";
    import SiteTable from "@/components/sites/SiteTable.svelte";
    import {Accordion, AccordionContent, AccordionItem, AccordionTrigger} from "@/components/ui/accordion";
    import JobTable from "@/components/jobs/JobTable.svelte";
    import CreateDocumentModal from "@/components/documents/CreateDocumentModal.svelte";
    import RelatedDocuments from "@/components/documents/RelatedDocuments.svelte";
    import {Button} from "@/components/ui/button";
    import CreateJobModal from "@/components/jobs/CreateJobModal.svelte";
    import * as Card from '@/components/ui/card';


    type Props = {
        site: Site;
        blocks: (Blocks & { fields: Field[] })[];
        jobBlocks: (Blocks & { fields: Field[] })[];
        documentBlocks: (Blocks & { fields: Field[] })[];
        relatedDocuments?: any[];
        entityFieldColumn?: string;
    };

    let {site, blocks, jobBlocks, documentBlocks, relatedDocuments, entityFieldColumn}: Props = $props();

    // Get the entity display name using the entityFieldColumn from the module
    const entityDisplayName = entityFieldColumn && (site as Record<string, any>)[entityFieldColumn]
        ? (site as Record<string, any>)[entityFieldColumn]
        : 'Site Details';

    let breadcrumbs = $state([
        {title: 'Sites', href: route('sites.index')},
        {title: entityDisplayName, href: route('sites.show', {site: site.id})},
    ]);

    let deleteForm = $state(useForm({}));

    let actionbuttons = $state([
        {
            title: 'Edit Site',
            href: undefined,
            onClick: openEditSiteModal,
            icon: Pencil,
        },
        {
            title: 'Add New Site',
            href: undefined,
            icon: Plus,
            onClick: openCreateSiteModal,
        },
        {
            title: 'Delete Site',
            actionType: 'delete',
            href: undefined,
            onClick: () => {
                $deleteForm.delete(route('sites.destroy', {site: site.id}));
            },
            icon: Trash,
        }
    ]);

    let editSiteModal = $state(false);

    function openEditSiteModal() {
        editSiteModal = true;
    }


    let showCreateSiteModal = $state(false);

    function openCreateSiteModal() {
        showCreateSiteModal = true;
    }

    let showCreateDocumentModal = $state(false);
    function openCreateDocumentModal() {
        showCreateDocumentModal = true;
    }

        let showCreateJobModal = $state(false);
    function openCreateJobModal(){
        showCreateJobModal = true;
    }



    let inlineFormInner: Record<string, any> = {}
    for (const block of blocks) {
        for (const field of block.fields) {
            inlineFormInner[field.column_name] = field.value;
        }
    }
    let inlineForm = $state(useForm(inlineFormInner));
    let editingFieldId: undefined | number = $state(undefined);
</script>
<!--<Debug {formInner} {form} {$form}/>-->

<svelte:head>
    <title>Site - {entityDisplayName}</title>
</svelte:head>
<AppLayout {breadcrumbs} {actionbuttons}>
    <FormContainer>
        {#each blocks as block}
            <FormBlock label={block.label} collapsible={true}>
                {#each block.fields as field}
                    <InlineEditable
                        field={field}
                        bind:editingFieldId={editingFieldId}
                        error={$inlineForm.errors[field.column_name]}
                        onSave={(newValue) => {
                                $inlineForm[field.column_name] = newValue;
                                $inlineForm.put(route("sites.update", { site }), {
                                    onSuccess: () => {
                                        editingFieldId = undefined;
                                    },
                                    onError: () => {

                                    }
                                });
                                // editingFieldId = undefined;
                            }
                        }
                        record={site}
                    />
                {/each}
            </FormBlock>
        {/each}
    </FormContainer>
    <Card.Title class="flex items-center justify-between">
        Related Jobs
        <Button
            onclick={openCreateJobModal}
            class="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent ">
            <Plus class="h-4 w-4"/> Add Job
        </Button>
    </Card.Title>
    <JobTable
        view="relatedList"
        relatedData={{
                          column : 'site_id',
                        record_id : site.id
                    }}
    />
    <CreateJobModal
        blocks={jobBlocks}
        bind:show={showCreateJobModal}
        relatedRecordId={site.id}
        relatedRecordLabel={site.site_name}
    />

    <RelatedDocuments {relatedDocuments} blocks={documentBlocks} onOpenCreate={openCreateDocumentModal}>
        <CreateDocumentModal
            blocks={documentBlocks}
            bind:show={showCreateDocumentModal}
            relatedRecordType="App\Models\Site"
            relatedRecordId={site.id}
            relatedRecordLabel={site.site_name}
        />
    </RelatedDocuments>

    <CreateSiteModal
        blocks={blocks}
        bind:show={showCreateSiteModal}
    />
    <EditSiteModal
        {blocks}
        bind:show={editSiteModal}
        {site}
    />

</AppLayout>
